import { camelCase } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useDeclarationContext } from 'views/declarations/common/DeclarationContext';
import { useProductTemplateContext } from 'utils/ProductTemplateContext';
import DeclarationVerticalNavigation from './components/DeclarationVerticalNavigation';
import scrollIntoView from 'scroll-into-view-if-needed';
import { FormDiv, StyledContent, StyledLayout } from './Form.styles';

type Origin = 'declarations' | 'manifests';

const paths = {
    masterDetails: (origin: Origin) =>
        origin === 'declarations' ? 'declarations/:declarationId' : 'manifests/:manifestId/:jobId/:declarationId',

    product: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/products/:productId'
            : 'manifests/:manifestId/:jobId/:declarationId/products/:productId',

    masterItem: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/master-items/:productId'
            : 'manifests/:manifestId/:jobId/:declarationId/master-items/:productId',

    houseConsignmentProduct: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/house-consignments/:consignmentId/:productId'
            : 'manifests/:manifestId/:jobId/:declarationId/house-consignments/:consignmentId/:productId',

    houseConsignment: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/house-consignments/:consignmentId'
            : 'manifests/:manifestId/:jobId/:declarationId/house-consignments/:consignmentId',

    viewOnlyMasterDetails: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/view-only'
            : 'manifests/:manifestId/:jobId/:declarationId/view-only',

    viewOnlyProduct: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/view-only/products/:productId'
            : 'manifests/:manifestId/:jobId/:declarationId/view-only/products/:productId',

    viewOnlyMasterItem: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/view-only/master-items/:productId'
            : 'manifests/:manifestId/:jobId/:declarationId/view-only/master-items/:productId',

    viewOnlyHouseConsignment: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/view-only/house-consignments/:consignmentId/'
            : 'manifests/:manifestId/:jobId/:declarationId/view-only/house-consignments/:consignmentId',

    viewOnlyHouseConsignmentProduct: (origin: Origin) =>
        origin === 'declarations'
            ? 'declarations/:declarationId/view-only/house-consignments/:consignmentId/:productId'
            : 'manifests/:manifestId/:jobId/:declarationId/view-only/house-consignments/:consignmentId/:productId',

    productTemplates: 'customs-declarations/:country/:internalType/products/:type/*',
} as const;

const normalize = (p: string) => {
    const clean = p.split('#')[0].split('?')[0];
    return clean !== '/' && clean.endsWith('/') ? clean.slice(0, -1) : clean;
};

const isExact = (pattern: string, pathname: string) =>
    !!matchPath(
        { path: `/${pattern.replace(/^\//, '')}`, end: true },
        normalize(pathname.startsWith('/') ? pathname : `/${pathname}`)
    );

type Props = {
    children: ReactNode;
    /**
     * Overrides the route table below. It only knows declaration, manifest and template routes, so
     * a form that lives anywhere else - the ICS2 arrival notification - has to say for itself that
     * it wants the navigation.
     */
    showNavigation?: boolean;
    contentId?: string;
};

const DeclarationFormContent = ({ children, showNavigation: showNavigationProp, contentId }: Props) => {
    const { pathname } = useLocation();
    const { form, cards, registerCard } = useDeclarationContext();
    const { inProductTemplate } = useProductTemplateContext();
    const [active, setActive] = useState(0);

    const inMasterDetails =
        isExact(paths.masterDetails('declarations'), pathname) || isExact(paths.masterDetails('manifests'), pathname);
    const inHouseConsignment =
        isExact(paths.houseConsignment('declarations'), pathname) ||
        isExact(paths.houseConsignment('manifests'), pathname);
    const inProducts =
        isExact(paths.product('declarations'), pathname) ||
        isExact(paths.product('manifests'), pathname) ||
        isExact(paths.masterItem('declarations'), pathname) ||
        isExact(paths.masterItem('manifests'), pathname) ||
        isExact(paths.houseConsignmentProduct('declarations'), pathname) ||
        isExact(paths.houseConsignmentProduct('manifests'), pathname);
    const inDeclarationTemplates = isExact('templates/edit/:templateId', pathname);
    const inDeclarationTemplatesAdd = isExact('templates/add', pathname);
    const inProductTemplates = isExact(paths.productTemplates, pathname);
    const inViewOnlyMasterDetails =
        isExact(paths.viewOnlyMasterDetails('declarations'), pathname) ||
        isExact(paths.viewOnlyMasterDetails('manifests'), pathname);
    const inViewOnlyHouseConsignment =
        isExact(paths.viewOnlyHouseConsignment('declarations'), pathname) ||
        isExact(paths.viewOnlyHouseConsignment('manifests'), pathname);
    const inViewOnlyProducts =
        isExact(paths.viewOnlyProduct('declarations'), pathname) ||
        isExact(paths.viewOnlyProduct('manifests'), pathname) ||
        isExact(paths.viewOnlyMasterItem('declarations'), pathname) ||
        isExact(paths.viewOnlyMasterItem('manifests'), pathname) ||
        isExact(paths.viewOnlyHouseConsignmentProduct('declarations'), pathname) ||
        isExact(paths.viewOnlyHouseConsignmentProduct('manifests'), pathname);

    /**
     * Construct the navigation items from the registered visible cards
     */
    const navItems = useMemo(() => {
        if (!cards) return [];
        const seen = new Set<string>();
        return cards
            .filter((card) => !card.section && (inProductTemplate ? 'product' : form) === card.form && card.visible)
            .filter((card) => {
                // The same card can be on screen more than once - inside a multiple
                // items card as well as on its own - but it needs one nav entry.
                if (seen.has(card.title)) return false;
                seen.add(card.title);
                return true;
            })
            .map((card) => ({ title: card.title, href: '', required: card.required }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerCard, cards]);

    const handleScroll = () => {
        const mainSection = document.getElementById(contentId ?? 'master-details-main-content');
        const container = document.getElementById('master-details-container');

        if (mainSection && container) {
            const sections = mainSection?.children;

            for (let i = 0; i < sections.length; i++) {
                const section = sections.item(i)!;

                if (isVisible(section, container)) {
                    const currentSectionTitle =
                        section.querySelector('.section-title')?.innerHTML ?? // Current design
                        section.querySelector('.ant-card-meta-title')?.innerHTML; // New design

                    const index = navItems.findIndex(
                        // eslint-disable-next-line no-loop-func
                        (navigationItem) => {
                            return (
                                navigationItem.title === currentSectionTitle ||
                                camelCase(navigationItem.title) === camelCase(currentSectionTitle)
                            );
                        }
                    );

                    setActive(index);

                    const node = document.querySelectorAll('.ant-steps-item')[index];
                    scrollIntoView(node, {
                        scrollMode: 'if-needed',
                        block: 'nearest',
                        inline: 'nearest',
                    });
                    return;
                }
            }
        }
    };

    const isVisible = function (element: Element, container: HTMLElement) {
        if (!element || !container) {
            return false;
        }

        const elementTop = element.getBoundingClientRect().top;
        const elementBottom = element.getBoundingClientRect().bottom;

        const containerTop = container.getBoundingClientRect().top;

        // 20 gives a bit of margin for the visible section of the element.
        // It adds 20 pixels to the top position of the element making it being perceived 20 pixels lower.
        const TOP_OFFSET = 20;
        const BOTTOM_OFFSET = 40;

        return elementTop + TOP_OFFSET > containerTop || elementBottom - BOTTOM_OFFSET > containerTop;
    };

    const showNavigation = useMemo(
        () =>
            showNavigationProp ??
            Boolean(
                inDeclarationTemplates ||
                    inDeclarationTemplatesAdd ||
                    inMasterDetails ||
                    inProducts ||
                    inProductTemplates ||
                    inViewOnlyMasterDetails ||
                    inViewOnlyProducts ||
                    inHouseConsignment ||
                    inViewOnlyHouseConsignment
            ),
        [
            inDeclarationTemplates,
            inDeclarationTemplatesAdd,
            inMasterDetails,
            inProducts,
            inProductTemplates,
            inViewOnlyMasterDetails,
            inViewOnlyProducts,
            inHouseConsignment,
            inViewOnlyHouseConsignment,
            showNavigationProp,
        ]
    );

    return (
        <StyledLayout>
            <DeclarationVerticalNavigation
                show={showNavigation}
                navigationItems={navItems}
                active={active}
                onChange={setActive}
            />
            <StyledContent id="master-details-container" onScroll={handleScroll}>
                <FormDiv>
                    <div id={contentId} style={{ width: '100%' }}>
                        {children}
                    </div>
                </FormDiv>
            </StyledContent>
        </StyledLayout>
    );
};

export default DeclarationFormContent;
